import { useState, useEffect } from "react";
import { useForm } from "react-hook-form";
import { supabase } from "@/integrations/supabase/client";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { useToast } from "@/hooks/use-toast";
import { UserCheck } from "lucide-react";
import { CANDIDATE_STATUS, EVALUATION_ROUNDS, USER_ROLES } from "@/types/enums";
import {
  formatCandidateStatus,
  formatEvaluationRound,
} from "@/utils/formatters";
import type { Tables } from "@/integrations/supabase/types";
import { useAuth } from "@/hooks/useAuth";
import { useAuthorization } from "@/hooks/useAuthorization";
import { FEEDBACK_SECTIONS } from "@/constants/feedbackTemplates";

type Candidate = Tables<"candidates"> & {
  job_positions?: { job_type?: string } | null;
};

const AUTO_ASSIGN_PANELIST_STATUSES = new Set<string>([
  CANDIDATE_STATUS.PASSED,
  CANDIDATE_STATUS.PENDING,
  CANDIDATE_STATUS.HOLD,
  CANDIDATE_STATUS.SELECTED,
  CANDIDATE_STATUS.OFFERED,
  CANDIDATE_STATUS.ACCEPTED,
  CANDIDATE_STATUS.JOINED,
]);

interface CandidateEvaluationFormProps {
  candidate: Candidate;
  onEvaluationComplete: () => void;
}

interface EvaluationFormData {
  round_name: string;
  score: number;
  feedback: Record<string, string>;
  status: string;
}

export function CandidateEvaluationForm({
  candidate,
  onEvaluationComplete,
}: CandidateEvaluationFormProps) {
  const { toast } = useToast();
  const [isSubmitting, setIsSubmitting] = useState(false);
  const { user } = useAuth();
  const { role } = useAuthorization();

  const sections =
    FEEDBACK_SECTIONS[candidate.job_positions?.job_type || ""] ||
    FEEDBACK_SECTIONS.default;

  const sectionKeys = sections.map((section) => ({
    label: section,
    key: section.toLowerCase().replace(/\s+/g, "_"),
  }));

  const defaultFeedbackValues = sectionKeys.reduce(
    (acc, { key }) => ({ ...acc, [key]: "" }),
    {} as Record<string, string>
  );
  const roundOptions =
    role === USER_ROLES.PANELIST
      ? [EVALUATION_ROUNDS.CAMPUS_INTERVIEW, EVALUATION_ROUNDS.OFFICE_TECHNICAL]
      : [
          EVALUATION_ROUNDS.CAMPUS_INTERVIEW,
          EVALUATION_ROUNDS.OFFICE_TECHNICAL,
          EVALUATION_ROUNDS.HR_FOUNDER,
        ];

  const defaultValues: EvaluationFormData = {
    round_name: "",
    score: 10,
    feedback: defaultFeedbackValues,
    status: "",
  };

  const {
    register,
    handleSubmit,
    reset,
    setValue,
    watch,
    formState: { errors },
  } = useForm<EvaluationFormData>({
    defaultValues,
  });

  const selectedRound = watch("round_name");

  useEffect(() => {
    register("status", { required: "Round Status is required" });
    register("round_name", { required: "Round Name is required" });
  }, [register]);

  const roundStatusOptions = [
    CANDIDATE_STATUS.PASSED,
    CANDIDATE_STATUS.FAILED,
    CANDIDATE_STATUS.HOLD,
  ];
  const interviewRoundFlow = [
    EVALUATION_ROUNDS.CAMPUS_INTERVIEW,
    EVALUATION_ROUNDS.OFFICE_TECHNICAL,
    EVALUATION_ROUNDS.HR_FOUNDER,
  ];

  const onSubmit = async (data: EvaluationFormData) => {
    setIsSubmitting(true);
    try {
      const formattedFeedback = sectionKeys
        .map(({ label, key }) => `- ${label}: ${data.feedback[key]}`)
        .join("\n");

      const { error: roundError } = await supabase
        .from("evaluation_rounds")
        .insert({
          candidate_id: candidate.id,
          round_name: data.round_name,
          score: data.score,
          feedback: formattedFeedback,
          status: data.status as any,
          evaluator_id: user?.id ?? null,
        });

      if (roundError) throw roundError;

      let nextCandidateStatus = data.status;
      let nextCurrentRound = data.round_name;

      if (data.status === CANDIDATE_STATUS.PASSED) {
        const currentRoundIndex = interviewRoundFlow.indexOf(
          data.round_name as (typeof interviewRoundFlow)[number]
        );

        if (currentRoundIndex === interviewRoundFlow.length - 1) {
          nextCandidateStatus = CANDIDATE_STATUS.OFFERED;
        } else if (currentRoundIndex >= 0) {
          nextCandidateStatus = CANDIDATE_STATUS.PENDING;
          nextCurrentRound = interviewRoundFlow[currentRoundIndex + 1];
        }
      }

      const candidateUpdateData: {
        status: any;
        current_round: string;
        interviewer?: string[] | null;
      } = {
        status: nextCandidateStatus as any,
        current_round: nextCurrentRound,
      };

      const hasExplicitInterviewer = Array.isArray(candidate.interviewer)
        ? candidate.interviewer.length > 0
        : typeof candidate.interviewer === "string" && candidate.interviewer.trim().length > 0;

      if (!hasExplicitInterviewer && AUTO_ASSIGN_PANELIST_STATUSES.has(nextCandidateStatus) && candidate.job_position_id) {
        const { data: jobPosition, error: jobPositionError } = await supabase
          .from("job_positions")
          .select("interviewer")
          .eq("id", candidate.job_position_id)
          .maybeSingle();

        if (jobPositionError) throw jobPositionError;

        const defaultInterviewers = Array.isArray(jobPosition?.interviewer)
          ? jobPosition.interviewer.filter((item): item is string => typeof item === "string" && item.trim().length > 0)
          : typeof jobPosition?.interviewer === "string" && jobPosition.interviewer.trim().length > 0
            ? [jobPosition.interviewer]
            : [];

        if (defaultInterviewers.length > 0) {
          candidateUpdateData.interviewer = defaultInterviewers;
        }
      }

      const { error: candidateError } = await supabase
        .from("candidates")
        .update(candidateUpdateData)
        .eq("id", candidate.id);

      if (candidateError) throw candidateError;

      toast({
        title: "Evaluation Submitted",
        description: "The candidate evaluation has been recorded successfully.",
      });

      reset(defaultValues);
      onEvaluationComplete();
    } catch (error) {
      console.error("Error submitting evaluation:", error);
      toast({
        title: "Error",
        description: "Failed to submit evaluation. Please try again.",
        variant: "destructive",
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <Card>
      <CardHeader>
        <CardTitle className="flex items-center gap-2">
          <UserCheck className="h-5 w-5" />
          Add Evaluation
        </CardTitle>
      </CardHeader>
      <CardContent>
        <form onSubmit={handleSubmit(onSubmit)} className="space-y-4">
          <div>
            <Label htmlFor="round_name">Round Name</Label>
            <Select
              onValueChange={(value) =>
                setValue("round_name", value, { shouldValidate: true })
              }
            >
              <SelectTrigger>
                <SelectValue placeholder="Select round" />
              </SelectTrigger>
              <SelectContent>
                {roundOptions.map((round) => (
                  <SelectItem key={round} value={round}>
                    {formatEvaluationRound(round)}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
            {errors.round_name && (
              <p className="mt-1 text-sm text-red-500">
                {errors.round_name.message}
              </p>
            )}
          </div>

          <div>
            <Label htmlFor="score">Score (10-100)</Label>
            <Input
              id="score"
              type="number"
              min="10"
              max="100"
              {...register("score", { required: true, min: 10, max: 100 })}
            />
          </div>

          <div className="space-y-2">
            <Label>Feedback</Label>
            {sectionKeys.map(({ label, key }) => (
              <div key={key}>
                <Label htmlFor={`feedback-${key}`}>{label}</Label>
                <Textarea
                  id={`feedback-${key}`}
                  {...register(`feedback.${key}`, { required: true })}
                  placeholder={`Provide feedback on ${label.toLowerCase()}...`}
                  rows={2}
                />
              </div>
            ))}
          </div>

          <div>
            <Label htmlFor="status">Round Status</Label>
            <Select
              onValueChange={(value) =>
                setValue("status", value, { shouldValidate: true })
              }
              defaultValue=""
            >
              <SelectTrigger>
                <SelectValue placeholder="Select status" />
              </SelectTrigger>
              <SelectContent>
                {roundStatusOptions.map((status) => (
                  <SelectItem key={status} value={status}>
                    {formatCandidateStatus(status)}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
            <p className="mt-1 text-xs text-muted-foreground">
              {selectedRound === EVALUATION_ROUNDS.HR_FOUNDER
                ? "Passing the final HR/Founder round will automatically move the candidate to Offered."
                : "Use Passed, Failed, or On Hold to record the result of the selected round."}
            </p>
            {errors.status && (
              <p className="mt-1 text-sm text-red-500">
                {errors.status.message}
              </p>
            )}
          </div>

          <Button type="submit" disabled={isSubmitting} className="w-full">
            {isSubmitting ? "Submitting..." : "Submit Evaluation"}
          </Button>
        </form>
      </CardContent>
    </Card>
  );
}
